import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Clipboard,
  Code2,
  Copy,
  ExternalLink,
  Globe2,
  KeyRound,
  Loader2,
  Mail,
  Plus,
  RefreshCcw,
  ShieldCheck,
  Webhook,
} from 'lucide-react';
import {
  applyReservePartnerCommand,
  buildReserveWebmasterMailto,
  buildReserveWidgetSnippet,
  fetchReserveMerchantIntegrations,
} from '../../lib/forfeoReserveProductionClient';
import { humanizeForfeoReserveError } from '../../lib/forfeoReserveEdgeError';
import type {
  ForfeoReserveMerchantIntegrationSnapshot,
  ForfeoReservePartnerApplication,
} from '../../../supabase/functions/_shared/forfeo-reserve-production';

type Props = {
  businessId: string;
  businessName: string;
};

export default function ReservePartnerIntegrationCenter({
  businessId,
  businessName,
}: Props) {
  const [snapshot, setSnapshot] =
    useState<ForfeoReserveMerchantIntegrationSnapshot | null>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [applicationName, setApplicationName] =
    useState(`Widget Reserve — ${businessName}`);
  const [origin, setOrigin] = useState('');
  const [webmasterEmail, setWebmasterEmail] = useState('');
  const [serverKey, setServerKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState('');

  const selectedApplication = useMemo(
    () =>
      snapshot?.applications.find(
        (application) => application.id === selectedApplicationId,
      ) || snapshot?.applications[0] || null,
    [snapshot, selectedApplicationId],
  );

  async function refresh() {
    setBusy(true);
    setMessage('');
    try {
      const result = await fetchReserveMerchantIntegrations(businessId);
      setSnapshot(result);
      if (!selectedApplicationId && result.applications[0]) {
        setSelectedApplicationId(result.applications[0].id);
      }
    } catch (error) {
      setMessage(humanizeForfeoReserveError(error instanceof Error ? error.message : String(error)));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    refresh();
  }, [businessId]);

  async function command(
    input: Parameters<typeof applyReservePartnerCommand>[0],
  ) {
    setBusy(true);
    setMessage('');
    setServerKey('');
    try {
      const result = await applyReservePartnerCommand(input);
      if (typeof result.serverKey === 'string') {
        setServerKey(result.serverKey);
      }
      await refresh();
    } catch (error) {
      setMessage(humanizeForfeoReserveError(error instanceof Error ? error.message : String(error)));
      setBusy(false);
    }
  }

  async function copyValue(label: string, value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(''), 1800);
  }

  const snippet = selectedApplication
    ? buildReserveWidgetSnippet({
        publicWidgetKey: selectedApplication.publicWidgetKey,
        accentColor: selectedApplication.accentColor,
      })
    : '';

  const primaryOrigin = selectedApplication?.origins[0]?.origin || '';

  return (
    <section className="space-y-6" data-commerce-panel="widget-api" data-forfeo-ui-v218="integrations">
      <header className="rounded-[2rem] border border-slate-200 bg-white p-7 text-slate-950 shadow-sm">
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-700">
          Forfeo Reserve
        </p>
        <h2 className="mt-2 text-3xl font-black">Widget et API partenaires</h2>
        <p className="mt-3 max-w-3xl text-sm text-slate-700">
          Générez le widget de réservation de votre entreprise, autorisez votre
          domaine et transmettez les instructions à votre webmaster. Les clés
          privées ne sont jamais envoyées par courriel.
        </p>
      </header>

      {message && (
        <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-900">
          <AlertTriangle className="shrink-0" size={19} />
          <span>{message}</span>
        </div>
      )}

      {serverKey && (
        <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <KeyRound className="mt-0.5 shrink-0 text-red-700" />
            <div className="min-w-0 flex-1">
              <h3 className="font-black text-red-900">
                Clé serveur révélée une seule fois
              </h3>
              <p className="mt-1 text-sm text-red-800">
                Copiez-la maintenant dans le gestionnaire de secrets de votre
                système. Ne la placez jamais dans un navigateur ou un courriel.
              </p>
              <div className="mt-4 flex gap-2">
                <code className="min-w-0 flex-1 overflow-x-auto rounded-xl bg-white p-3 text-xs text-red-950">
                  {serverKey}
                </code>
                <button
                  type="button"
                  onClick={() => copyValue('server-key', serverKey)}
                  className="rounded-xl bg-red-700 px-4 font-black text-white"
                >
                  {copied === 'server-key' ? <Check /> : <Copy />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-2 text-sm font-black text-blue-800">
          <Loader2 className="animate-spin" size={18} />
          Synchronisation…
        </div>
      )}

      {!snapshot || snapshot.applications.length === 0 ? (
        <section className="rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm">
          <h3 className="text-xl font-black">Créer votre première intégration</h3>
          <p className="mt-2 text-sm text-slate-700">
            Le widget de base peut être inclus dans votre forfait. L’API serveur,
            les webhooks, la marque blanche et les domaines supplémentaires
            dépendent de vos droits.
          </p>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <input
              value={applicationName}
              onChange={(event) => setApplicationName(event.target.value)}
              className="min-h-12 flex-1 rounded-xl border border-slate-300 px-4"
              maxLength={120}
            />
            <button
              type="button"
              disabled={busy || applicationName.trim().length < 2}
              onClick={() =>
                command({
                  kind: 'create_application',
                  businessId,
                  name: applicationName,
                  planCode: 'standard',
                  billingTier: 'included',
                })
              }
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 font-black text-white disabled:opacity-50"
            >
              <Plus size={18} /> Créer
            </button>
          </div>
        </section>
      ) : (
        <div className="grid gap-5 xl:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-black">Applications</h3>
              <button
                type="button"
                onClick={refresh}
                className="rounded-lg p-2 text-slate-700 hover:bg-slate-100"
                aria-label="Actualiser"
              >
                <RefreshCcw size={17} />
              </button>
            </div>
            <div className="mt-4 grid gap-2">
              {snapshot.applications.map((application) => (
                <button
                  key={application.id}
                  type="button"
                  onClick={() => setSelectedApplicationId(application.id)}
                  className={`rounded-xl border p-3 text-left ${
                    selectedApplication?.id === application.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <span className="block font-black">{application.name}</span>
                  <span className="mt-1 block text-xs text-slate-700">
                    {application.status} · {application.installationStatus}
                  </span>
                </button>
              ))}
            </div>
          </aside>

          {selectedApplication && (
            <div className="space-y-5">
              <ApplicationSummary
                application={selectedApplication}
                entitlements={snapshot.entitlements}
              />

              <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-lg font-black">
                  <Globe2 className="text-blue-700" /> Domaine autorisé
                </h3>
                <p className="mt-2 text-sm text-slate-700">
                  Ajoutez uniquement l’origine du site, sans chemin. Exemple :
                  https://www.exemple.ca
                </p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <input
                    value={origin}
                    onChange={(event) => setOrigin(event.target.value)}
                    placeholder="https://www.votreentreprise.ca"
                    className="min-h-12 flex-1 rounded-xl border border-slate-300 px-4"
                  />
                  <button
                    type="button"
                    disabled={busy || !origin.trim()}
                    onClick={() =>
                      command({
                        kind: 'add_origin',
                        businessId,
                        applicationId: selectedApplication.id,
                        origin,
                      }).then(() => setOrigin(''))
                    }
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 font-black text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Plus size={17} /> Autoriser
                  </button>
                </div>

                <div className="mt-4 grid gap-2">
                  {selectedApplication.origins.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="font-bold">{entry.origin}</p>
                        <p className="text-xs text-slate-700">
                          {entry.verificationStatus}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          command({
                            kind: 'remove_origin',
                            businessId,
                            applicationId: selectedApplication.id,
                            origin: entry.origin,
                          })
                        }
                        className="text-xs font-black text-red-700"
                      >
                        Retirer
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-lg font-black">
                  <Code2 className="text-indigo-700" /> Code du widget
                </h3>
                <p className="mt-2 text-sm text-slate-700">
                  Ce code contient seulement une clé publique. Il peut être
                  transmis au webmaster.
                </p>
                <pre className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-800">
                  {snippet}
                </pre>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => copyValue('snippet', snippet)}
                    className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-indigo-700 px-4 font-black text-white"
                  >
                    {copied === 'snippet' ? <Check size={17} /> : <Clipboard size={17} />}
                    Copier le code
                  </button>
                  <a
                    href={`/reserve/widget/${selectedApplication.publicWidgetKey}?locale=fr&accent=${encodeURIComponent(
                      selectedApplication.accentColor || '#5b21b6',
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 px-4 font-black text-slate-700"
                  >
                    <ExternalLink size={17} /> Prévisualiser
                  </a>
                </div>
              </section>

              <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-lg font-black">
                  <Mail className="text-emerald-700" /> Envoyer au webmaster
                </h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
                  <input
                    type="email"
                    value={webmasterEmail}
                    onChange={(event) => setWebmasterEmail(event.target.value)}
                    placeholder="webmaster@agence.ca"
                    className="min-h-12 rounded-xl border border-slate-300 px-4"
                  />
                  <a
                    href={
                      webmasterEmail && primaryOrigin
                        ? buildReserveWebmasterMailto({
                            webmasterEmail,
                            businessName,
                            domain: primaryOrigin,
                            snippet,
                          })
                        : undefined
                    }
                    aria-disabled={!webmasterEmail || !primaryOrigin}
                    className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-xl px-5 font-black text-white ${
                      webmasterEmail && primaryOrigin
                        ? 'bg-emerald-700'
                        : 'pointer-events-none bg-slate-300'
                    }`}
                  >
                    <Mail size={17} /> Préparer le courriel
                  </a>
                </div>
              </section>

              <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-lg font-black">
                  <KeyRound className="text-amber-700" /> API serveur
                </h3>
                <p className="mt-2 text-sm text-slate-700">
                  La clé privée est réservée aux systèmes serveur. Elle est
                  révélée une seule fois et son empreinte seulement est stockée.
                </p>
                <button
                  type="button"
                  disabled={busy || !snapshot.entitlements.apiEnabled}
                  onClick={() =>
                    command({
                      kind: 'rotate_server_key',
                      businessId,
                      applicationId: selectedApplication.id,
                    })
                  }
                  className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl bg-amber-600 px-4 font-black text-white disabled:opacity-40"
                >
                  <KeyRound size={17} /> Générer ou faire une rotation
                </button>
              </section>

              <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-lg font-black">
                  <Webhook className="text-fuchsia-700" /> Webhooks
                </h3>
                <p className="mt-2 text-sm text-slate-700">
                  Confirmation, modification et annulation. Les secrets de
                  signature ne sont jamais affichés dans le navigateur.
                </p>
                <div className="mt-4 grid gap-2">
                  {selectedApplication.webhooks.length === 0 ? (
                    <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-700">
                      Aucun webhook configuré.
                    </p>
                  ) : (
                    selectedApplication.webhooks.map((webhook) => (
                      <div
                        key={webhook.id}
                        className="rounded-xl border border-slate-200 p-3"
                      >
                        <p className="font-bold">{webhook.endpointUrl}</p>
                        <p className="mt-1 text-xs text-slate-700">
                          {webhook.status} · {webhook.pendingCount} en attente ·{' '}
                          {webhook.deadLetterCount} en échec définitif
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ApplicationSummary({
  application,
  entitlements,
}: {
  application: ForfeoReservePartnerApplication;
  entitlements: ForfeoReserveMerchantIntegrationSnapshot['entitlements'];
}) {
  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm" data-commerce-panel-v219="widget-api" data-commerce-v220-panel="widget-api">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-slate-700">
            {application.billingTier}
          </p>
          <h3 className="mt-1 text-2xl font-black">{application.name}</h3>
          <p className="mt-2 text-sm text-slate-700">
            {application.hourlyQuota} requêtes/heure · {application.dailyQuota}{' '}
            requêtes/jour
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
          {[
            ['API', entitlements.apiEnabled],
            ['Webhooks', entitlements.webhooksEnabled],
            ['Marque blanche', entitlements.whiteLabelEnabled],
            ['Installation', entitlements.installationServiceAvailable],
          ].map(([label, enabled]) => (
            <div
              key={String(label)}
              className={`rounded-xl px-3 py-2 font-black ${
                enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'
              }`}
            >
              {String(label)}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2 text-xs font-black">
        <span className="rounded-full bg-blue-50 px-3 py-1 text-blue-700">
          {application.status}
        </span>
        <span className="rounded-full bg-violet-50 px-3 py-1 text-violet-700">
          {application.installationStatus}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-slate-700">
          <ShieldCheck size={13} />
          {application.lastSmokeTestStatus || 'not_run'}
        </span>
      </div>
    </section>
  );
}
